import React, { useState, useEffect } from 'react';
import PageLayout from '../layouts/PageLayout.js';

function Admin() {
  const [accountingData, setAccountingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccountingData();
  }, []);

  const fetchAccountingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching accounting data...');
      const apiBase = window.DECODEDMUSIC_CONFIG?.apiEndpoint || 'https://5gp7mnnjwf.execute-api.us-west-2.amazonaws.com/prod';
      const url = `${apiBase}/accounting?artistId=rdv01`;
      
      console.log('📡 API URL:', url);
      const response = await fetch(url);
      
      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', response.headers);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Accounting data received:', data);
      setAccountingData(data);
    } catch (err) {
      console.error('❌ Error fetching accounting data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="professional-container">
          <div className="professional-header">
            <h1>🎵 DecodedMusic Business Dashboard</h1>
            <p>Loading QuickBooks-style accounting...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="professional-container">
          <div className="professional-header">
            <h1 style={{ color: '#ef4444' }}>❌ Dashboard Error</h1>
            <p>Error loading accounting data: {error}</p>
            <button onClick={fetchAccountingData} className="professional-button professional-button-primary">
              Retry
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Debug logging
  console.log('🐛 Admin component state:', { loading, error, hasData: !!accountingData });

  return (
    <PageLayout>
      <div className="professional-container">
        {/* Navigation Breadcrumb */}
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem 1rem', 
          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
            <button 
              onClick={() => window.location.href = '/#/workspace'}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--accent-color)', 
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.9rem'
              }}
            >
              🏠 Dashboard
            </button>
            <span style={{ color: 'var(--text-secondary)' }}>→</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>Admin Console</span>
          </nav>
        </div>

        <div className="professional-header">
          <h1>🎵 DecodedMusic Business Dashboard</h1>
          <p style={{ color: 'var(--accent-color)', fontWeight: 'bold', margin: '0.5rem 0 0 0' }}>
            Created by artists, for artists
          </p>
        </div>

        {accountingData && (
          <>
            {/* Debug Info */}
            <div className="professional-card" style={{ marginBottom: '1rem', backgroundColor: 'rgba(0, 255, 136, 0.1)', border: '1px solid var(--accent-color)' }}>
              <div className="professional-card-header">
                <h3 style={{ color: 'var(--accent-color)', margin: '0' }}>🐛 Debug Info</h3>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0' }}>
                Data loaded: {accountingData ? 'Yes' : 'No'} | 
                Artist: {accountingData?.artist} | 
                Period: {accountingData?.accountingPeriod} |
                Has Income Statement: {accountingData?.incomeStatement ? 'Yes' : 'No'}
              </p>
            </div>

            {/* Executive Summary */}
            <div className="professional-card" style={{ marginBottom: '2rem' }}>
              <div className="professional-card-header">
                <h2 className="professional-card-title">📊 Executive Summary</h2>
              </div>
              <div className="professional-grid-4">
                <div className="professional-card">
                  <h3 style={{ color: 'var(--success-color)', margin: '0', textAlign: 'center' }}>Net Income</h3>
                  <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0', textAlign: 'center' }}>
                    {formatCurrency(accountingData.incomeStatement?.netIncome || 0)}
                  </p>
                </div>
                <div className="professional-card">
                  <h3 style={{ color: 'var(--success-color)', margin: '0', textAlign: 'center' }}>Total Revenue</h3>
                  <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0', textAlign: 'center' }}>
                    {formatCurrency(accountingData.incomeStatement?.revenue?.totalIncome || 0)}
                  </p>
                </div>
                <div className="professional-card">
                  <h3 style={{ color: 'var(--success-color)', margin: '0', textAlign: 'center' }}>Cash Position</h3>
                  <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0', textAlign: 'center' }}>
                    {formatCurrency(accountingData.balanceSheet?.assets?.currentAssets?.cash || 0)}
                  </p>
                </div>
                <div className="professional-card">
                  <h3 style={{ color: 'var(--success-color)', margin: '0', textAlign: 'center' }}>Profit Margin</h3>
                  <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0', textAlign: 'center' }}>
                    {accountingData.financialRatios?.profitMargin || '0%'}
                  </p>
                </div>
              </div>
            </div>

            {/* Income Statement */}
            <div className="professional-card" style={{ marginBottom: '2rem' }}>
              <div className="professional-card-header">
                <h2 className="professional-card-title">💰 Income Statement</h2>
              </div>
              <div className="professional-grid-2">
                <div className="professional-card">
                  <h3 style={{ color: 'var(--success-color)', marginBottom: '1rem' }}>Revenue Streams</h3>
                  {accountingData.incomeStatement?.revenue?.streaming && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>🎵 Streaming Revenue</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>Spotify: {formatCurrency(accountingData.incomeStatement.revenue.streaming.spotify)}</p>
                        <p>Apple Music: {formatCurrency(accountingData.incomeStatement.revenue.streaming.appleMusic)}</p>
                        <p>YouTube: {formatCurrency(accountingData.incomeStatement.revenue.streaming.youtube)}</p>
                        <p style={{ fontWeight: 'bold', color: 'var(--success-color)' }}>
                          Total: {formatCurrency(accountingData.incomeStatement.revenue.streaming.total)}
                        </p>
                      </div>
                    </div>
                  )}
                  {accountingData.incomeStatement?.revenue?.subscribers && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>👥 Subscriber Revenue</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>Monthly Plans: {formatCurrency(accountingData.incomeStatement.revenue.subscribers.monthlySubscriptions)}</p>
                        <p>Annual Plans: {formatCurrency(accountingData.incomeStatement.revenue.subscribers.annualSubscriptions)}</p>
                        <p>Premium Tiers: {formatCurrency(accountingData.incomeStatement.revenue.subscribers.premiumTiers)}</p>
                        <p style={{ fontWeight: 'bold', color: 'var(--success-color)' }}>
                          Total: {formatCurrency(accountingData.incomeStatement.revenue.subscribers.total)}
                        </p>
                      </div>
                    </div>
                  )}
                  <div style={{ color: 'var(--text-secondary)' }}>
                    <p>🎬 Sync Licensing: {formatCurrency(accountingData.incomeStatement?.revenue?.syncLicensing || 0)}</p>
                    <p>👕 Merchandise: {formatCurrency(accountingData.incomeStatement?.revenue?.merchandise || 0)}</p>
                    <p>🎤 Live Performances: {formatCurrency(accountingData.incomeStatement?.revenue?.livePerformances || 0)}</p>
                  </div>
                </div>
                <div className="professional-card">
                  <h3 style={{ color: 'var(--error-color)', marginBottom: '1rem' }}>Business Expenses</h3>
                  {accountingData.incomeStatement?.expenses?.marketing && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>📢 Marketing</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>Facebook Ads: {formatCurrency(accountingData.incomeStatement.expenses.marketing.facebook)}</p>
                        <p>Instagram Ads: {formatCurrency(accountingData.incomeStatement.expenses.marketing.instagram)}</p>
                        <p>Google Ads: {formatCurrency(accountingData.incomeStatement.expenses.marketing.google)}</p>
                        <p style={{ fontWeight: 'bold', color: 'var(--error-color)' }}>
                          Total: {formatCurrency(accountingData.incomeStatement.expenses.marketing.total)}
                        </p>
                      </div>
                    </div>
                  )}
                  <div style={{ color: 'var(--text-secondary)' }}>
                    <p>🎵 Distribution: {formatCurrency(accountingData.incomeStatement?.expenses?.distribution || 0)}</p>
                    <p>🎧 Equipment: {formatCurrency(accountingData.incomeStatement?.expenses?.equipment || 0)}</p>
                    <p>💻 Software: {formatCurrency(accountingData.incomeStatement?.expenses?.software || 0)}</p>
                    <p>👨‍💼 Professional Services: {formatCurrency(accountingData.incomeStatement?.expenses?.professional || 0)}</p>
                  </div>
                </div>
              </div>
              <div className="professional-card" style={{ 
                marginTop: '1.5rem',
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                border: '1px solid var(--success-color)'
              }}>
                <h3 style={{ color: 'var(--success-color)', margin: '0' }}>NET INCOME</h3>
                <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--success-color)', margin: '0.5rem 0' }}>
                  {formatCurrency(accountingData.incomeStatement?.netIncome || 0)}
                </p>
              </div>
            </div>

            {/* Cash Flow Statement */}
            {accountingData.cashFlowStatement && (
              <div className="professional-card" style={{ marginBottom: '2rem' }}>
                <div className="professional-card-header">
                  <h2 className="professional-card-title">💸 Cash Flow Statement</h2>
                </div>
                <div className="professional-grid-3">
                  <div className="professional-card">
                    <h3 style={{ color: 'var(--success-color)' }}>Operating Activities</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Net Income: {formatCurrency(accountingData.cashFlowStatement.operatingActivities?.netIncome || 0)}</p>
                    <p style={{ color: 'var(--text-secondary)' }}>Adjustments: {formatCurrency(accountingData.cashFlowStatement.operatingActivities?.adjustments?.totalAdjustments || 0)}</p>
                    <p style={{ fontWeight: 'bold', color: 'var(--success-color)' }}>
                      Net Cash: {formatCurrency(accountingData.cashFlowStatement.operatingActivities?.netCashFromOperations || 0)}
                    </p>
                  </div>
                  <div className="professional-card">
                    <h3 style={{ color: 'var(--accent-color)' }}>Investing Activities</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Equipment: {formatCurrency(accountingData.cashFlowStatement.investingActivities?.equipmentPurchases || 0)}</p>
                    <p style={{ color: 'var(--text-secondary)' }}>Studio Upgrades: {formatCurrency(accountingData.cashFlowStatement.investingActivities?.studioUpgrades || 0)}</p>
                    <p style={{ fontWeight: 'bold', color: 'var(--accent-color)' }}>
                      Net Cash: {formatCurrency(accountingData.cashFlowStatement.investingActivities?.netCashFromInvesting || 0)}
                    </p>
                  </div>
                  <div className="professional-card">
                    <h3 style={{ color: 'var(--warning-color)' }}>Financing Activities</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Subscriptions: {formatCurrency(accountingData.cashFlowStatement.financingActivities?.artistSubscriptions || 0)}</p>
                    <p style={{ fontWeight: 'bold', color: 'var(--warning-color)' }}>
                      Net Cash: {formatCurrency(accountingData.cashFlowStatement.financingActivities?.netCashFromFinancing || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Balance Sheet */}
            {accountingData.balanceSheet && (
              <div className="professional-card" style={{ marginBottom: '2rem' }}>
                <div className="professional-card-header">
                  <h2 className="professional-card-title">📋 Balance Sheet</h2>
                </div>
                <div className="professional-grid-2">
                  <div className="professional-card">
                    <h3 style={{ color: 'var(--success-color)', marginBottom: '1rem' }}>Assets</h3>
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>Current Assets</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>💰 Cash: {formatCurrency(accountingData.balanceSheet.assets.currentAssets?.cash || 0)}</p>
                        <p>📋 Accounts Receivable: {formatCurrency(accountingData.balanceSheet.assets.currentAssets?.accountsReceivable || 0)}</p>
                        <p>📦 Inventory: {formatCurrency(accountingData.balanceSheet.assets.currentAssets?.inventory || 0)}</p>
                      </div>
                    </div>
                    <div>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>Fixed Assets</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>🎧 Equipment: {formatCurrency(accountingData.balanceSheet.assets.fixedAssets?.equipment || 0)}</p>
                        <p>🎵 Music Catalog: {formatCurrency(accountingData.balanceSheet.assets.fixedAssets?.musicCatalog || 0)}</p>
                      </div>
                    </div>
                    <p style={{ fontWeight: 'bold', color: 'var(--success-color)', fontSize: '1.2rem', marginTop: '1rem' }}>
                      Total Assets: {formatCurrency(accountingData.balanceSheet.assets?.totalAssets || 0)}
                    </p>
                  </div>
                  <div className="professional-card">
                    <h3 style={{ color: 'var(--error-color)', marginBottom: '1rem' }}>Liabilities & Equity</h3>
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>Liabilities</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>📄 Accounts Payable: {formatCurrency(accountingData.balanceSheet.liabilities.currentLiabilities?.accountsPayable || 0)}</p>
                        <p>📋 Accrued Expenses: {formatCurrency(accountingData.balanceSheet.liabilities.currentLiabilities?.accruedExpenses || 0)}</p>
                        <p style={{ fontWeight: 'bold', color: 'var(--error-color)' }}>
                          Total: {formatCurrency(accountingData.balanceSheet.liabilities?.totalLiabilities || 0)}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h4 style={{ color: 'var(--text-primary)', margin: '0.5rem 0' }}>Owner's Equity</h4>
                      <div style={{ paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                        <p>💼 Owner Equity: {formatCurrency(accountingData.balanceSheet.equity?.ownerEquity || 0)}</p>
                        <p>📈 Retained Earnings: {formatCurrency(accountingData.balanceSheet.equity?.retainedEarnings || 0)}</p>
                        <p style={{ fontWeight: 'bold', color: 'var(--success-color)' }}>
                          Total Equity: {formatCurrency(accountingData.balanceSheet.equity?.totalEquity || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Financial Ratios */}
            {accountingData.financialRatios && (
              <div className="professional-card" style={{ marginBottom: '2rem' }}>
                <div className="professional-card-header">
                  <h2 className="professional-card-title">📊 Financial Ratios</h2>
                </div>
                <div className="professional-grid-4">
                  <div className="professional-card" style={{ textAlign: 'center' }}>
                    <h4 style={{ color: 'var(--success-color)', margin: '0' }}>Profit Margin</h4>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0' }}>
                      {accountingData.financialRatios.profitMargin}
                    </p>
                  </div>
                  <div className="professional-card" style={{ textAlign: 'center' }}>
                    <h4 style={{ color: 'var(--success-color)', margin: '0' }}>ROE</h4>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0' }}>
                      {accountingData.financialRatios.returnOnEquity}
                    </p>
                  </div>
                  <div className="professional-card" style={{ textAlign: 'center' }}>
                    <h4 style={{ color: 'var(--success-color)', margin: '0' }}>Current Ratio</h4>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0' }}>
                      {accountingData.financialRatios.currentRatio}
                    </p>
                  </div>
                  <div className="professional-card" style={{ textAlign: 'center' }}>
                    <h4 style={{ color: 'var(--success-color)', margin: '0' }}>Debt-to-Equity</h4>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0.5rem 0' }}>
                      {accountingData.financialRatios.debtToEquityRatio}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="professional-card" style={{ textAlign: 'center' }}>
              <div className="professional-card-header">
                <h2 className="professional-card-title" style={{ color: 'var(--accent-color)' }}>
                  🎵 BUZZ THE HYPE - Professional Accounting for Independent Artists
                </h2>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.5rem 0' }}>
                Created by artists, for artists | Enterprise-grade financial management
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                <button 
                  onClick={() => window.location.href = '/#/workspace'}
                  style={{ 
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    backgroundColor: 'var(--accent-color)', 
                    color: '#000', 
                    border: 'none', 
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🏠 Back to Dashboard
                </button>
                <button 
                  onClick={fetchAccountingData}
                  style={{ 
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                    color: 'var(--text-primary)', 
                    border: '1px solid rgba(255, 255, 255, 0.2)', 
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🔄 Refresh Data
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default Admin;
